import React, { useCallback, useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  getTabbedContentBoxSx,
  getTabbedBlockMobileScrollStyles,
} from "../../../mmk-section/styles";
import {
  CONTENT_BLOCK_TYPES_BLOCKED_FOR_TTS,
  type ContentBlock,
  type Message,
  type MessagePrev,
  type MessageTypeColor,
  type TabbedMessageContent,
} from "../../../../types";
import { useChatStore } from "../../../../stores/chatStore";
import {
  identifyContentKind,
  buildLessonIdsForBadges,
} from "../../../../helpers/contentKind";
import { CustomMarkdown } from "../../../custom-markdown/custom-markdown";
import { AuthenticatedImage } from "../../../authenticated-media/authenticated-image";
import { AuthenticatedFileLink } from "../../../authenticated-media/authenticated-file-link";
import { AudioPlayer } from "../audio-player/audio-player";
import { VideoPlayer } from "../video-player/video-player";
import { FlashCardMessage } from "../flash-card-message/flash-card-message";
import { FlippingCardMessage } from "../flipping-card-message/flipping-card-message";
import { ImageCollection } from "../image-messages/image-collection/image-collection";
import { FlashImageCollection } from "../image-messages/flash-image-collection/flash-image-collection";
import MessageButtons from "../message-buttons/message-buttons";
import { InteractiveQuestionBlock } from "../../../interactive-questions/interactive-question-block";
import { MessageCharts } from "../message-charts/message-charts";
import { FeedbackMessage } from "../text-message/feetback-message/feetback-message";
import { isSuggestionTabLabel } from "../../../../helpers/messageHelpers";
import { blockHasContent } from "./normalize";
import { getMarkdownStyle } from "./content-block-markdown-styles";

const hash = (s: string) =>
  Math.abs(
    s.split("").reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0),
  ).toString(36);

export interface ContentBlockRendererProps {
  blocks: ContentBlock[];
  message: Message;
  showGlobalTitleChip?: boolean;
  activeTabId: string;
  activeTabLabel?: string;
  isBot: boolean;
  variant: MessageTypeColor;
  currentReaction?: string;
  isSaved?: boolean;
  markedIds: Record<string, string>;
  isTags?: string[];
  handleReactionClick: (reactionKey: string) => void;
  handleAddToFavorite: () => void;
  handleRemoveTag: (tag: string) => Promise<void>;
  setActiveFlashCardText?: React.Dispatch<React.SetStateAction<string>>;
  setActiveFlippingCardText?: React.Dispatch<React.SetStateAction<string>>;
  setLargeHigth?: React.Dispatch<React.SetStateAction<boolean>>;
  nextMessage?: Message;
  onQuizAnswered?: (tabId: string) => void;
  tabLevelTranscription?: string;
  tabLevelTranscriptionTimed?: Message["transcription_timed"];
}

export const ContentBlockRenderer: React.FC<ContentBlockRendererProps> = ({
  blocks,
  message,
  showGlobalTitleChip = false,
  activeTabId,
  activeTabLabel = "",
  isBot,
  variant,
  currentReaction,
  isSaved,
  markedIds,
  isTags,
  handleReactionClick,
  handleAddToFavorite,
  handleRemoveTag,
  setActiveFlashCardText = () => {},
  setActiveFlippingCardText = () => {},
  setLargeHigth = () => {},
  nextMessage,
  onQuizAnswered,
  tabLevelTranscription = "",
  tabLevelTranscriptionTimed,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const markdownStyle = useMemo(() => getMarkdownStyle(isDark), [isDark]);
  const baseId = message.message_id;
  const lessons = useChatStore((s) => s.lessons);
  const lessonsByScore = useChatStore((s) => s.lessonsByScore);
  const lessonsByCompetence = useChatStore((s) => s.lessonsByCompetence);
  const lessonsToDo = useChatStore((s) => s.lessonsToDo);
  const messages = useChatStore((s) => s.messages);
  const reactedLessonCache = useChatStore((s) => s.reactedLessonCache);
  const favoriteTabLabelsCache = useChatStore((s) => s.favoriteTabLabelsCache);
  const crashCourses = useChatStore((s) => s.crashCourses);
  const pillTitle = useMemo(() => {
    const lessonIds = buildLessonIdsForBadges(
      [lessons, lessonsByScore, lessonsByCompetence, lessonsToDo],
      messages,
      reactedLessonCache,
      favoriteTabLabelsCache,
      [message as MessagePrev],
    );
    const crashCourseIds = new Set(crashCourses.map((c) => c.message_id));
    const kind = identifyContentKind(message as MessagePrev, {
      lessonIds,
      crashCourseIds,
      favoriteTabLabelsCache,
    });
    return kind === "lesson" || kind === "crash_course";
  }, [
    message,
    lessons,
    lessonsByScore,
    lessonsByCompetence,
    lessonsToDo,
    messages,
    reactedLessonCache,
    favoriteTabLabelsCache,
    crashCourses,
  ]);

  const isAudioTab = useMemo(() => {
    const withContent = blocks.filter(blockHasContent);
    return (
      withContent.length > 0 && withContent.every((b) => b.type === "audio")
    );
  }, [blocks]);

  const reactionProps = useMemo(
    () => ({
      isBot,
      currentReaction,
      isSaved,
      markedIds,
      isTags,
      handleReactionClick,
      handleAddToFavorite,
      handleRemoveTag,
    }),
    [
      isBot,
      currentReaction,
      isSaved,
      markedIds,
      isTags,
      handleReactionClick,
      handleAddToFavorite,
      handleRemoveTag,
    ],
  );

  const markdownAudioComponent = useCallback(
    (props: { src?: string; node?: { properties?: { src?: string } } }) => {
      const src = props.src ?? props.node?.properties?.src ?? "";
      return (
        <Box
          sx={{
            my: 1,
            width: "100%",
            display: "flex",
            justifyContent: "stretch",
          }}
        >
          <AudioPlayer
            {...reactionProps}
            audioUrl={src}
            text={showGlobalTitleChip ? "" : message.title || ""}
            messageId={`${baseId}-tab-${activeTabId}-md-a-${hash(src || "empty")}`}
            embedded
            pillTitle={pillTitle}
            hideTitle={showGlobalTitleChip}
          />
        </Box>
      );
    },
    [
      baseId,
      activeTabId,
      reactionProps,
      message.title,
      pillTitle,
      showGlobalTitleChip,
    ],
  );

  const markdownImgComponent = useCallback(
    ({ src, alt, ...props }: React.ImgHTMLAttributes<HTMLImageElement>) => (
      <AuthenticatedImage
        src={src}
        alt={alt || "Image"}
        sx={{
          width: "100%",
          maxWidth: "100%",
          height: "auto",
          borderRadius: 1,
          marginTop: 1,
          marginBottom: 1,
          display: "block",
          objectFit: "contain",
        }}
      />
    ),
    [],
  );

  const isReferencesTab = activeTabLabel.toLowerCase().trim() === "references";

  const contentBoxSx = useMemo(
    () => getTabbedContentBoxSx(theme, isReferencesTab),
    [isReferencesTab, theme],
  );

  const renderBlock = useCallback(
    (block: ContentBlock, index: number) => {
      switch (block.type) {
        case "text":
          if (!block.content?.trim()) return null;
          return (
            <Box key={index} sx={markdownStyle}>
              <CustomMarkdown
                components={{
                  audio: markdownAudioComponent,
                  img: markdownImgComponent,
                }}
              >
                {block.content}
              </CustomMarkdown>
            </Box>
          );

        case "image":
          if (!block.file_url?.trim()) return null;
          return (
            <Box key={index} sx={{ my: 1, width: "100%" }}>
              <AuthenticatedImage
                src={block.file_url}
                alt={block.alt || "Image"}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: 1,
                  display: "block",
                  objectFit: "contain",
                }}
              />
            </Box>
          );

        case "audio":
          const audioUrl =
            block.audio_url ?? (block as { file_url?: string }).file_url ?? "";
          const audioMsgId = `${baseId}-tab-${activeTabId}-a-${audioUrl ? hash(audioUrl) : `empty-${index}`}`;
          const effectiveTranscription =
            block.transcription?.trim() || tabLevelTranscription?.trim() || "";
          const effectiveTimedTranscription =
            block.transcription_timed ?? tabLevelTranscriptionTimed;
          return (
            <Box
              key={index}
              sx={{
                mt: 1,
                mb: 0.5,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
              }}
            >
              <AudioPlayer
                {...reactionProps}
                audioUrl={audioUrl}
                text={
                  showGlobalTitleChip || isAudioTab
                    ? ""
                    : block.title?.trim() || message.title || ""
                }
                messageId={audioMsgId}
                embedded
                pillTitle={pillTitle}
                hideTitle={showGlobalTitleChip || isAudioTab}
                transcription={effectiveTranscription || undefined}
                transcriptionTimed={effectiveTimedTranscription}
              />
            </Box>
          );

        case "video":
          if (!block.video_url) return null;
          const videoMsgId = `${baseId}-tab-${activeTabId}-v-${hash(block.video_url)}`;
          return (
            <Box key={index} sx={{ my: 1 }}>
              <VideoPlayer
                {...reactionProps}
                videoUrl={block.video_url}
                title={block.title}
                messageId={videoMsgId}
                embedded
              />
            </Box>
          );

        case "pdf":
          if (!block.pdf_url) return null;
          return (
            <Box key={index} sx={{ my: 1 }}>
              <AuthenticatedFileLink
                href={block.pdf_url}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  borderRadius: 1,
                  backgroundColor: "action.hover",
                  color: "primary.main",
                  textDecoration: "none",
                  "&:hover": { backgroundColor: "action.selected" },
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  📄 {block.title || "Open PDF"}
                </Typography>
              </AuthenticatedFileLink>
            </Box>
          );

        case "image_collection":
          if (!block.image_collection?.length) return null;
          const imageMsg: Message = {
            ...message,
            type: "image_collection",
            title: "",
            image_collection: block.image_collection,
          };
          return (
            <Box key={index} sx={{ my: 1, width: "100%" }}>
              <ImageCollection
                message={imageMsg}
                {...reactionProps}
                variant={variant}
                setLargeHigth={setLargeHigth}
                nextMessage={nextMessage}
                embedded
              />
            </Box>
          );

        case "flash_card_image_collection":
          if (!block.image_collection?.length) return null;
          const flashImageMsg: Message = {
            ...message,
            type: "flash_card_image_collection",
            title: "",
            image_collection: block.image_collection,
          };
          return (
            <Box key={index} sx={{ my: 1, width: "100%" }}>
              <FlashImageCollection
                message={flashImageMsg}
                {...reactionProps}
                variant={variant}
                embedded
              />
            </Box>
          );

        case "flash_card":
          if (!block.flash_card?.length) return null;
          const flashMsg: Message = {
            ...message,
            type: "flash_card",
            flash_card: block.flash_card,
          };
          return (
            <Box
              key={index}
              sx={{
                my: 1,
                width: "100%",
                ...getTabbedBlockMobileScrollStyles(theme),
              }}
            >
              <FlashCardMessage
                message={flashMsg}
                {...reactionProps}
                variant={variant}
                nextMessage={nextMessage}
                setLargeHigth={setLargeHigth}
                setActiveFlashCardText={setActiveFlashCardText}
                embedded
              />
            </Box>
          );

        case "flipping_card":
          if (!block.flipping_card?.length) return null;
          const flipMsg: Message = {
            ...message,
            type: "flipping_card",
            flipping_card: block.flipping_card,
          };
          return (
            <Box
              key={index}
              sx={{
                my: 1,
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
              }}
            >
              <FlippingCardMessage
                message={flipMsg}
                {...reactionProps}
                variant={variant}
                setActiveFlippingCardText={setActiveFlippingCardText}
                embedded
              />
            </Box>
          );

        case "quiz": {
          const quizText = block.text || "";
          const quizButtons = block.buttons || [];
          const quizMsg: Message = {
            ...message,
            type: "button",
            text: quizText,
            buttons: quizButtons,
            state: block.state,
          };
          return (
            <Box key={index} sx={{ my: 1, width: "100%" }}>
              {quizButtons.length > 0 && (
                <MessageButtons
                  message={quizMsg}
                  buttons={quizButtons}
                  messageId={baseId}
                  userId={message.user_id}
                  tabId={activeTabId}
                  state={block.state}
                  {...reactionProps}
                  embedded
                  onQuizAnswered={() => onQuizAnswered?.(activeTabId)}
                />
              )}
            </Box>
          );
        }

        case "buttons": {
          const buttonsText = block.text || "";
          const buttonsButtons = block.buttons || [];
          const isSuggestionTab = isSuggestionTabLabel(activeTabLabel);
          const buttonsMsg: Message = {
            ...message,
            type: "button",
            text: buttonsText,
            buttons: buttonsButtons,
            state: block.state,
          };

          return (
            <Box key={index} sx={{ my: 1, width: "100%" }}>
              {buttonsButtons.length > 0 && (
                <MessageButtons
                  message={buttonsMsg}
                  buttons={buttonsButtons}
                  messageId={baseId}
                  userId={message.user_id}
                  tabId={activeTabId}
                  state={block.state}
                  {...reactionProps}
                  embedded
                  isSuggestionTab={isSuggestionTab}
                  onQuizAnswered={() => onQuizAnswered?.(activeTabId)}
                />
              )}
            </Box>
          );
        }

        case "chart":
          if (!block.chart_name || !block.items?.length) return null;
          const chartMsg: Message = {
            ...message,
            type: "charts",
            chart_name: block.chart_name,
            items: block.items,
          };
          return (
            <Box key={index} sx={{ my: 1, width: "100%" }}>
              <MessageCharts message={chartMsg} />
            </Box>
          );
        case "fill_in_blank":
        case "ordering":
        case "matching":
        case "ordered_response":
          return (
            <Box key={`${activeTabId}-${index}-${block.type}`} sx={{ my: 1, width: "100%" }}>
              <InteractiveQuestionBlock
                key={`${activeTabId}-${index}-${block.type}`}
                block={block}
                blockIndex={index}
                messageId={baseId}
                userId={message.user_id}
                tabId={activeTabId}
                onQuizAnswered={() => onQuizAnswered?.(activeTabId)}
              />
            </Box>
          );

        case "feedback":
          const feedbackMsg: Message = {
            ...message,
            type: "text",
            message_type: "feedback",
            text: block.text ?? "",
            title: block.title,
            score: block.score ?? 0,
          };
          return (
            <Box key={index} sx={{ my: 1, width: "100%" }}>
              <FeedbackMessage
                message={feedbackMsg}
                {...reactionProps}
                isBot={isBot}
                variant={variant}
                embedded
                animationKey={activeTabId}
              />
            </Box>
          );
        default:
          return null;
      }
    },
    [
      message,
      activeTabId,
      activeTabLabel,
      baseId,
      theme,
      isBot,
      markdownAudioComponent,
      markdownImgComponent,
      markdownStyle,
      reactionProps,
      variant,
      setActiveFlashCardText,
      setActiveFlippingCardText,
      setLargeHigth,
      nextMessage,
      onQuizAnswered,
      tabLevelTranscription,
      tabLevelTranscriptionTimed,
      pillTitle,
      showGlobalTitleChip,
      isAudioTab,
    ],
  );

  return (
    <Box sx={contentBoxSx}>
      {blocks.map((block, i) => renderBlock(block, i))}
    </Box>
  );
};

// Get preview text from tab (for ContentCard / onActiveTabContentChange)
export function getTabPreviewText(tab: TabbedMessageContent): string {
  if (tab.content_blocks?.length) {
    const texts: string[] = [];
    for (const b of tab.content_blocks) {
      if (b.type === "text" && b.content) texts.push(b.content);
      if (b.type === "image" && b.file_url) texts.push(b.file_url);
      if (b.type === "quiz" && b.text) texts.push(b.text);
      if (b.type === "buttons" && b.text) texts.push(b.text);
      if (b.type === "flash_card" && b.flash_card?.length) {
        const flatCards = b.flash_card.flat();
        flatCards.forEach((card) => {
          if (!card || typeof card !== "object") return;
          if ("title" in card && card.title) texts.push(String(card.title));
          if ("text" in card && card.text) texts.push(String(card.text));
        });
      }
      if (b.type === "flipping_card" && b.flipping_card?.length) {
        b.flipping_card.forEach((card) => {
          if (!card || typeof card !== "object") return;
          if (card.title) texts.push(card.title);
          if (card.front_text) texts.push(card.front_text);
          if (card.back_text) texts.push(card.back_text);
        });
      }
      if (b.type === "image_collection" && b.image_collection?.length) {
        b.image_collection.forEach((img) => {
          if (!img || typeof img !== "object") return;
          if (img.description) texts.push(img.description);
        });
      }
      if (
        b.type === "flash_card_image_collection" &&
        b.image_collection?.length
      ) {
        b.image_collection.forEach((img) => {
          if (!img || typeof img !== "object") return;
          if (img.description) texts.push(img.description);
        });
      }
      if (b.type === "video" && b.title) texts.push(b.title);
      if (b.type === "pdf" && b.title) texts.push(b.title);
      if (b.type === "chart" && b.chart_name) texts.push(b.chart_name);
      if (b.type === "fill_in_blank" && b.title) texts.push(b.title);
      if (b.type === "ordering" && b.title) texts.push(b.title);
      if (b.type === "matching" && b.title) texts.push(b.title);
      if (b.type === "ordered_response" && b.title) texts.push(b.title);
      if (b.type === "feedback" && b.text) texts.push(b.text);
      if (b.type === "audio" && b.title?.trim()) texts.push(b.title.trim());
      if (b.type === "audio" && b.transcription?.trim()) {
        texts.push(b.transcription.trim());
      }
    }
    const fromBlocks = texts.join("\n\n").trim();
    const tabTranscription = tab.transcription?.trim();
    if (tabTranscription) {
      return [fromBlocks, tabTranscription].filter(Boolean).join("\n\n").trim();
    }
    return fromBlocks;
  }
  const legacy = tab.content?.trim() || "";
  const tabTranscription = tab.transcription?.trim();
  if (tabTranscription) {
    return [legacy, tabTranscription].filter(Boolean).join("\n\n").trim();
  }
  return legacy;
}

// Text passed to TTS for the active tab; ignores media/chart/pdf/image-collection blocks.
export function getTabTtsSpeakableText(tab: TabbedMessageContent): string {
  if (tab.content_blocks?.length) {
    const texts: string[] = [];
    for (const b of tab.content_blocks) {
      if (CONTENT_BLOCK_TYPES_BLOCKED_FOR_TTS.has(b.type)) continue;
      if (b.type === "text" && b.content) texts.push(b.content);
      if (b.type === "quiz" && b.text) texts.push(b.text);
      if (b.type === "buttons" && b.text) texts.push(b.text);
      if (b.type === "flash_card" && b.flash_card?.length) {
        const flatCards = b.flash_card.flat();
        flatCards.forEach((card) => {
          if (!card || typeof card !== "object") return;
          if ("title" in card && card.title) texts.push(String(card.title));
          if ("text" in card && card.text) texts.push(String(card.text));
        });
      }
      if (b.type === "flipping_card" && b.flipping_card?.length) {
        b.flipping_card.forEach((card) => {
          if (!card || typeof card !== "object") return;
          if (card.title) texts.push(card.title);
          if (card.front_text) texts.push(card.front_text);
          if (card.back_text) texts.push(card.back_text);
        });
      }
      if (b.type === "feedback" && b.text) texts.push(b.text);
    }
    const fromBlocks = texts.join("\n\n").trim();
    const tabTranscription = tab.transcription?.trim();
    if (tabTranscription && fromBlocks) {
      return [fromBlocks, tabTranscription].filter(Boolean).join("\n\n").trim();
    }
    return fromBlocks;
  }
  const legacy = tab.content?.trim() || "";
  const tabTranscription = tab.transcription?.trim();
  if (tabTranscription && legacy) {
    return [legacy, tabTranscription].filter(Boolean).join("\n\n").trim();
  }
  return legacy;
}
